<!-- src/components/layout/AdminLayout.vue -->
<template>
  <div class="min-h-screen bg-gray-100">
    <!-- Sidebar mobile -->
    <transition name="slide">
      <div v-if="sidebarOpen" 
           class="fixed inset-0 flex z-40 md:hidden" 
           role="dialog" 
           aria-modal="true">
        <div class="fixed inset-0 bg-gray-600 bg-opacity-75" 
             aria-hidden="true" 
             @click="sidebarOpen = false"></div>
        
        <div class="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-primary-600">
          <div class="absolute top-0 right-0 -mr-12 pt-2">
            <button type="button" 
                    class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    @click="sidebarOpen = false">
              <span class="sr-only">Fermer</span>
              <svg class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <!-- Marque UltraLINK, comme le bandeau de l'app (05/10/2026) ; le logo de la société
               reste sur la page de connexion. -->
          <div class="flex-shrink-0 px-4 py-3">
            <marque-ultra-link fond="marine" sous-titre="Administration" />
          </div>
          
          <div class="mt-5 flex-1 h-0 overflow-y-auto">
            <nav class="px-2 space-y-1">
              <router-link to="/admin" 
                           class="sidebar-link" 
                           :class="{ active: currentRoute.path === '/admin' }">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Tableau de bord
              </router-link>
              
              <router-link to="/admin/clients" 
                           class="sidebar-link" 
                           :class="{ active: currentRoute.path.startsWith('/admin/clients') }">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Clients
              </router-link>

              <router-link to="/admin/events"
                           class="sidebar-link"
                           :class="{ active: currentRoute.path.startsWith('/admin/events') }">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Événements
              </router-link>

              <router-link to="/admin/firmware"
                           class="sidebar-link"
                           :class="{ active: currentRoute.path.startsWith('/admin/firmware') }">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
                Firmware
              </router-link>
              <router-link to="/admin/donnees"
                           class="sidebar-link"
                           :class="{ active: currentRoute.path.startsWith('/admin/donnees') }">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                </svg>
                Données mobiles
              </router-link>

              <router-link to="/admin/administrateurs"
                           class="sidebar-link"
                           :class="{ active: currentRoute.path.startsWith('/admin/administrateurs') }">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Administrateurs
              </router-link>

              <router-link to="/securite"
                           class="sidebar-link"
                           :class="{ active: currentRoute.path.startsWith('/securite') }">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                Sécurité du compte
              </router-link>

              <!-- Ajout du lien vers le changement de mot de passe -->
              <router-link to="/change-password" 
                           class="sidebar-link">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Changer mot de passe
              </router-link>
              
              <a @click="logout" class="sidebar-link cursor-pointer">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Déconnexion
              </a>
            </nav>
          </div>
        </div>
      </div>
    </transition>
    
    <!-- Sidebar desktop -->
    <div class="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
      <div class="flex-1 flex flex-col min-h-0 bg-primary-600">
        <div class="flex flex-shrink-0 items-center px-4 py-4 border-b border-primary-500/40">
          <marque-ultra-link fond="marine" sous-titre="Administration" />
        </div>
        
        <div class="flex-1 flex flex-col overflow-y-auto">
          <nav class="flex-1 px-2 py-4 space-y-1">
            <router-link to="/admin" 
                         class="sidebar-link" 
                         :class="{ active: currentRoute.path === '/admin' }">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Tableau de bord
            </router-link>
            
            <router-link to="/admin/clients" 
                         class="sidebar-link" 
                         :class="{ active: currentRoute.path.startsWith('/admin/clients') }">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Clients
            </router-link>

            <router-link to="/admin/events"
                         class="sidebar-link"
                         :class="{ active: currentRoute.path.startsWith('/admin/events') }">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Événements
            </router-link>

            <router-link to="/admin/firmware"
                         class="sidebar-link"
                         :class="{ active: currentRoute.path.startsWith('/admin/firmware') }">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              Firmware
            </router-link>
            <router-link to="/admin/donnees"
                         class="sidebar-link"
                         :class="{ active: currentRoute.path.startsWith('/admin/donnees') }">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
              </svg>
              Données mobiles
            </router-link>

            <router-link to="/admin/administrateurs"
                         class="sidebar-link"
                         :class="{ active: currentRoute.path.startsWith('/admin/administrateurs') }">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Administrateurs
            </router-link>

            <router-link to="/securite"
                         class="sidebar-link"
                         :class="{ active: currentRoute.path.startsWith('/securite') }">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              Sécurité du compte
            </router-link>

            <!-- Ajout du lien vers le changement de mot de passe -->
            <router-link to="/change-password" 
                         class="sidebar-link">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Changer mot de passe
            </router-link>
            
            <a @click="logout" class="sidebar-link cursor-pointer">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Déconnexion
            </a>
          </nav>
        </div>
      </div>
    </div>
    
    <!-- Main content -->
    <div class="md:pl-64 flex flex-col flex-1">
      <div class="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow">
        <button type="button" 
                class="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
                @click="sidebarOpen = true">
          <span class="sr-only">Ouvrir la sidebar</span>
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        
        <div class="flex-1 px-4 flex justify-between">
          <div class="flex-1 flex items-center">
            <h1 class="text-2xl font-semibold text-gray-900">{{ title }}</h1>
          </div>
          
          <div class="ml-4 flex items-center md:ml-6">
            <div class="flex items-center">
              <span class="text-sm font-medium text-gray-700 mr-2">{{ user.username }}</span>
              <span class="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white">
                {{ userInitials }}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <main class="flex-1 pb-8">
        <div class="py-6 px-4 sm:px-6 lg:px-8">
          <slot></slot>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import MarqueUltraLink from '@/components/common/MarqueUltraLink.vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';

export default defineComponent({
  name: 'AdminLayout',
  components: { MarqueUltraLink },

  props: {
    title: {
      type: String,
      default: 'Tableau de bord'
    }
  },
  
  setup() {
    const store = useStore();
    const router = useRouter();
    const currentRoute = useRoute();
    
    const sidebarOpen = ref(false);
    
    const user = computed(() => store.getters['auth/user']);
    
    const userInitials = computed(() => {
      if (!user.value || !user.value.username) return '';
      
      return user.value.username.charAt(0).toUpperCase();
    });
    
    const logout = async () => {
      try {
        // Déconnexion avec attente de la fin de l'action
        await store.dispatch('auth/logout');
        
        // Redirection forcée avec reload pour s'assurer que tout est rechargé
        window.location.href = '/login';
      } catch (error) {
        console.error('Erreur lors de la déconnexion:', error);
        // Redirection de secours en cas d'erreur
        window.location.href = '/login';
      }
    };
    
    return {
      sidebarOpen,
      user,
      userInitials,
      currentRoute,
      logout
    };
  }
});
</script>

<style lang="scss" scoped>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease-out;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
